import { ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
  isFetching?: boolean;
  hideShowingText?: boolean;
  compact?: boolean;
}

export function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  hasNext,
  hasPrev,
  onPageChange,
  isFetching,
  hideShowingText = false,
  compact = false,
}: PaginationProps) {
  // Generate visible page numbers with ellipsis
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    const showAround = 1;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= page - showAround && i <= page + showAround)
      ) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== 'ellipsis') {
        pages.push('ellipsis');
      }
    }
    return pages;
  };

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  if (totalPages <= 1) {
    if (hideShowingText) return null;
    return (
      <div className={clsx(
        'flex items-center',
        !compact && 'justify-between py-3 px-2 border-t border-dark-border'
      )}>
        <div className="text-sm text-dark-text-secondary">
          Showing {total} item{total !== 1 ? 's' : ''}
        </div>
      </div>
    );
  }

  // Compact mode: just show the pagination controls inline
  if (compact) {
    return (
      <div className="flex items-center gap-3">
        {!hideShowingText && (
          <span className="text-sm text-dark-text-secondary whitespace-nowrap">
            Showing {startItem}-{endItem} of {total}
          </span>
        )}

        <div className="flex items-center gap-1">
          <button
            className={clsx(
              'p-1.5 rounded-sm hover:bg-dark-surface transition-colors',
              (!hasPrev || isFetching) && 'opacity-50 cursor-not-allowed'
            )}
            disabled={!hasPrev || isFetching}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {getPageNumbers().map((p, idx) =>
            p === 'ellipsis' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-dark-text-secondary">
                ...
              </span>
            ) : (
              <button
                key={p}
                className={clsx(
                  'min-w-[32px] h-8 px-2 rounded-sm text-sm transition-colors',
                  p === page
                    ? 'bg-primary-600 text-white'
                    : 'hover:bg-dark-surface text-dark-text-secondary',
                  isFetching && 'opacity-50'
                )}
                onClick={() => onPageChange(p)}
                disabled={isFetching}
              >
                {p}
              </button>
            )
          )}

          <button
            className={clsx(
              'p-1.5 rounded-sm hover:bg-dark-surface transition-colors',
              (!hasNext || isFetching) && 'opacity-50 cursor-not-allowed'
            )}
            disabled={!hasNext || isFetching}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-3 px-2 border-t border-dark-border">
      {!hideShowingText && (
        <div className="text-sm text-dark-text-secondary">
          Showing {startItem}-{endItem} of {total}
        </div>
      )}
      {hideShowingText && <div />}

      <div className="flex items-center gap-1">
        <button
          className={clsx(
            'p-1.5 rounded-sm hover:bg-dark-surface transition-colors',
            (!hasPrev || isFetching) && 'opacity-50 cursor-not-allowed'
          )}
          disabled={!hasPrev || isFetching}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {getPageNumbers().map((p, idx) =>
          p === 'ellipsis' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-dark-text-secondary">
              ...
            </span>
          ) : (
            <button
              key={p}
              className={clsx(
                'min-w-[32px] h-8 px-2 rounded-sm text-sm transition-colors',
                p === page
                  ? 'bg-primary-600 text-white'
                  : 'hover:bg-dark-surface text-dark-text-secondary',
                isFetching && 'opacity-50'
              )}
              onClick={() => onPageChange(p)}
              disabled={isFetching}
            >
              {p}
            </button>
          )
        )}

        <button
          className={clsx(
            'p-1.5 rounded-sm hover:bg-dark-surface transition-colors',
            (!hasNext || isFetching) && 'opacity-50 cursor-not-allowed'
          )}
          disabled={!hasNext || isFetching}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
